"use client";

import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Button} from "@/components/ui/button";
import type {WorkflowDebugAction, WorkflowRecovery} from "@/hooks/useWorkflowTurns";
import type {ConversationMessage, MessageContentFunctionCall, MessageContentFunctionResult} from "@/services/wingman";
import {collectOmegaProgress, parseOmegaResult, type OmegaPresentation} from "@/services/wingman/omega-results";
import CompiledWorkflowCard from "./CompiledWorkflowCard";
import CompiledWorkflowRun from "./CompiledWorkflowRun";
import type {ChatWorkflowPublication, ConfirmedWorkflowDraft} from "./useChatWorkflowPublication";
import WorkflowActivity from "./WorkflowActivity";
import WorkflowBuildProgress from "./WorkflowBuildProgress";
import {readPublicationWorkflow} from "./workflowPublicationSummary";
import {readPublicationReceipt} from "./workflowPublicationReceipt";

type Props = {
   messages: ConversationMessage[];
   presentation: OmegaPresentation;
   generationError?: string | null;
   selectedId?: string;
   /** Omitted when the chat edits the workflow open beside it. */
   onSelect?: (id: string) => void;
   openInEditor: boolean;
   onOpenInEditor?: () => void;
   disabled: boolean;
   busy: boolean;
   recovery: WorkflowRecovery;
   onRecover: () => Promise<void>;
   onTest: (id: string) => Promise<boolean>;
   onDebug: (sessionId: string, action: WorkflowDebugAction) => Promise<boolean>;
   onPublish: (draft: ConfirmedWorkflowDraft) => Promise<ChatWorkflowPublication>;
};

/** Workflows this conversation saved or tested, projected from Wingman's Omega tool results. */
export default function CompiledWorkflowPanel(props: Props) {
   const {t} = useTranslation("ask");
   const {activities, runs, defaultWorkflowId} = props.presentation;
   const publicationIds = useMemo(() => publicationWorkflowIds(props.messages), [props.messages]);
   const workflows = props.presentation.workflows.filter(workflow => !publicationIds.has(workflow.id));
   const progress = useMemo(() => collectOmegaProgress(props.messages), [props.messages]);
   const isWorking = props.busy && props.recovery === "ready" && !props.generationError && progress.steps.length > 0;
   const selectedId = props.selectedId ?? defaultWorkflowId;
   const workflow = workflows.find(item => item.id === selectedId);
   const otherWorkflows = workflows.filter(item => item.id !== selectedId);
   const unavailable = props.disabled || props.busy || props.recovery !== "ready";
   const hasWorkflowActivity = workflows.length > 0 || activities.some(activity => !activity.pending) || runs.length > 0;
   // A blocked recovery disables the composer, so it is explained even in a chat without workflows.
   if (!hasWorkflowActivity && !isWorking && props.recovery !== "blocked") return null;

   return (
      <section className="mx-3 my-4 space-y-3 md:mx-6" aria-label={t("workflowPanel.title")}>
         {isWorking && <WorkflowBuildProgress progress={progress} />}
         {props.generationError && (
            <p role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
               {props.generationError}
            </p>
         )}
         {props.recovery !== "ready" && (
            <output className="flex flex-wrap items-center gap-2 text-sm text-700">
               {props.recovery === "reconciling" ? t("workflowPanel.reconciling") : t("workflowPanel.recoveryBlocked")}
               {props.recovery === "blocked" && (
                  <Button variant="link" size="sm" className="h-auto p-0" onClick={() => void props.onRecover()}>
                     {t("workflowPanel.reloadHistory")}
                  </Button>
               )}
            </output>
         )}
         {workflow && (
            <CompiledWorkflowCard
               key={workflow.id}
               workflow={workflow}
               openInEditor={props.openInEditor}
               onOpenInEditor={props.onOpenInEditor}
               unavailable={unavailable}
               onTest={props.onTest}
               onPublish={props.onPublish}
            />
         )}
         {otherWorkflows.length > 0 && (
            <details className="rounded-lg border border-300 text-sm">
               <summary className="cursor-pointer px-3 py-2 text-600">{t("workflowPanel.otherWorkflows", {count: otherWorkflows.length})}</summary>
               <div className="divide-y divide-300 border-t border-300">
                  {otherWorkflows.map(item => (
                     <div key={item.id} className="flex items-center justify-between gap-3 p-3">
                        <div className="min-w-0">
                           <p className="break-words font-medium text-900">{item.name}</p>
                           <p className="mt-1 text-xs text-500">{t("workflowPanel.steps", {count: item.graph.nodes.length})}</p>
                        </div>
                        {props.onSelect && <Button variant="outline" size="sm" disabled={unavailable} onClick={() => props.onSelect?.(item.id)}>{t("workflowPanel.review")}</Button>}
                     </div>
                  ))}
               </div>
            </details>
         )}
         {runs.map((run) => (
            <CompiledWorkflowRun key={run.id} run={run} unavailable={unavailable} onDebug={props.onDebug} />
         ))}
         {!isWorking && <WorkflowActivity activities={activities} busy={props.busy} />}
      </section>
   );
}

/** Preserve message order so a later saved draft starts a new review. */
function publicationWorkflowIds(messages: ConversationMessage[]) {
   const publicationIds = new Set<string>();
   const publicationCalls = new Map<string, string | null | undefined>();
   for (const part of messages.flatMap(message => message.contents)) {
      if (part.type === "function_call") trackPublicationReview(part, publicationCalls, publicationIds);
      if (part.type === "function_result") trackPublicationResult(part, publicationCalls, publicationIds);
   }
   return publicationIds;
}

function trackPublicationReview(part: MessageContentFunctionCall, calls: Map<string, string | null | undefined>, ids: Set<string>) {
   if (part.functionName !== "omega_publishcustomerworkflow" || !part.approval || part.approval.previewUnavailable || part.presentationUnavailable) return;
   const workflow = readPublicationWorkflow(part.parameters);
   if (workflow) calls.set(part.callId, workflow.id);
   if (workflow?.id) ids.add(workflow.id);
}

function trackPublicationResult(part: MessageContentFunctionResult, calls: Map<string, string | null | undefined>, ids: Set<string>) {
   if (calls.has(part.callId)) {
      const receipt = readPublicationReceipt(part.result, calls.get(part.callId));
      if (receipt) ids.add(receipt.id);
   }
   if (part.functionName === "omega_savedraftcustomerworkflow") {
      const result = parseOmegaResult(part.result);
      const body = result?.body as {data?: {id?: unknown}} | undefined;
      if (result?.ok && typeof body?.data?.id === "string") ids.delete(body.data.id);
   }
}
